import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import HabboImage from '../../../components/HabboImage';
import StaffNotFound from '../StaffNotFound';
import { resolveCosmeticUrl } from '../../../services/profileColorService';
import { getAdminCircleBgs, saveCircleBg, deleteCircleBg } from '../../../services/api';

const input = 'w-full border border-gray-300 bg-white px-2 py-[5px] text-[13px] text-gray-800 outline-none focus:border-maroon';
const label = "block font-['Jersey_10'] text-[14px] uppercase tracking-[1px] text-maroon";
const btn = "font-['Jersey_10'] uppercase text-[15px] tracking-[1px] px-3 py-2 rounded transition-all";
const shadow = { boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' };

const PREVIEW_NAME = 'FranklyOrigins';

// Profile layout maths, so grid-sized tiles come out at their true pixel size:
// Wrapper max-w-[1400px] minus Profile's p-6 (48) = 1352 of content.
// grid-cols-[360px_minmax(0,1fr)] with gap-6 (24) gives left 360, right 968.
// ContentBox adds a 1px border each side and these boxes use padding="p-0".
const NARROW_COL = 358;
const WIDE_COL = 966;

// A tile is one cell of its grid, so its width is the column width less the
// wrapper's p-px and the gap-px between cells, divided by the column count.
const topFriendCell = (colWidth) => (colWidth - 2 - 4) / 5;

// Friends tab: auto-fit minmax(110px, 1fr) settles on 8 columns at WIDE_COL.
const FRIENDS_TAB_CELL = (WIDE_COL - 2 - 7) / 8;

// Friend tiles colour their label from safeHexOrFallback(bg). An image background
// is never a hex so it always falls back to #5A2929, whose luminance sits under
// the 0.16 threshold, meaning image backgrounds always get the light label.
const FRIEND_LABEL_COLOUR = '#f5f5f0';

function Panel({ title, children, right }) {
    return (
        <section className="border border-gray-300 bg-offwhite" style={{ boxShadow: '0 3px 0 0 rgba(0,0,0,0.5)' }}>
            <header className="flex items-center justify-between gap-2 bg-maroon px-3 py-[6px]">
                <h3 className="font-['Jersey_10'] text-[16px] uppercase tracking-[1px] text-white">{title}</h3>
                {right}
            </header>
            <div className="p-3">{children}</div>
        </section>
    );
}

function Occurrence({ title, note, children }) {
    return (
        <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
                <span className="font-['Jersey_10'] text-[14px] uppercase tracking-[1px] text-maroon">{title}</span>
                {note && <span className="text-[11px] text-gray-500">{note}</span>}
            </div>
            <div className="overflow-x-auto rounded border border-black/15 bg-[#ededed]/60 p-3">
                {children}
            </div>
        </div>
    );
}

// One cell of the Top Friends! grid, at the width that grid gives it.
function TopFriendTile({ bgStyle, colWidth, wide }) {
    return (
        <div
            className="relative flex flex-col items-center justify-center overflow-hidden"
            style={{ ...bgStyle, width: topFriendCell(colWidth), aspectRatio: wide ? '1 / 1' : '3 / 4' }}
        >
            <div className="pointer-events-none relative top-[-8px]">
                <HabboImage
                    habbo={PREVIEW_NAME}
                    hotel="COM"
                    size={wide ? 'b' : 'a'}
                    direction={2}
                    head_direction={3}
                    gesture="sml"
                    style={{ imageRendering: 'pixelated', display: 'block' }}
                />
            </div>
            <div
                className="pointer-events-none absolute bottom-1 left-0 right-0 truncate px-0.5 text-center font-['Jersey_10'] text-base leading-none tracking-widest"
                style={{ color: FRIEND_LABEL_COLOUR }}
            >
                {PREVIEW_NAME}
            </div>
        </div>
    );
}

// The Friends tab is a different component: a fixed 64px circle with a dark label
// underneath, inside an auto-fit grid of minmax(110px, 1fr) cells.
function FriendsTabTile({ bgStyle }) {
    return (
        <div
            className="relative flex flex-col items-center gap-1 bg-offwhite p-2"
            style={{ width: FRIENDS_TAB_CELL }}
        >
            <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full" style={bgStyle}>
                <HabboImage habbo={PREVIEW_NAME} hotel="COM" size="a" direction={2} head_direction={3} gesture="sml" />
            </div>
            <div className="w-full truncate px-1 text-center font-['Jersey_10'] text-lg leading-none tracking-widest text-coal">
                {PREVIEW_NAME}
            </div>
        </div>
    );
}

// Every tile below is copied from the component that actually draws the
// background, at the size it renders on a desktop profile.
function BackgroundPreviews({ bgStyle }) {
    return (
        <div className="flex flex-col gap-4">
            <Occurrence title="Avatars" note="fixed sizes, identical everywhere">
                <div className="flex flex-wrap items-end gap-5">
                    <div className="flex flex-col items-center gap-1">
                        <div className="flex h-[92px] w-[92px] items-center justify-center overflow-hidden rounded border border-black/50" style={bgStyle}>
                            <HabboImage
                                habbo={PREVIEW_NAME}
                                hotel="COM"
                                size="b"
                                direction={2}
                                head_direction={2}
                                gesture="sml"
                                className="relative mt-14"
                                style={{ imageRendering: 'pixelated', scale: 2 }}
                                alt=""
                            />
                        </div>
                        <span className="text-[11px] text-gray-500">Profile picture</span>
                    </div>

                    <div className="flex flex-col items-center gap-1">
                        <div className="flex h-[96px] w-[96px] shrink-0 items-center justify-center overflow-hidden rounded border-2 border-black/10" style={bgStyle}>
                            <HabboImage habbo={PREVIEW_NAME} hotel="COM" size="a" direction={2} head_direction={2} gesture="sml" headonly="true" />
                        </div>
                        <span className="text-[11px] text-gray-500">Post</span>
                    </div>

                    <div className="flex flex-col items-center gap-1">
                        <div className="relative h-[48px] w-[48px] shrink-0 overflow-hidden rounded border border-black/20" style={bgStyle}>
                            <HabboImage
                                habbo={PREVIEW_NAME}
                                hotel="COM"
                                size="a"
                                direction={2}
                                head_direction={2}
                                gesture="sml"
                                headonly
                                className="absolute left-1/2 top-5 max-w-none -translate-x-1/2 -translate-y-1/2"
                            />
                        </div>
                        <span className="text-[11px] text-gray-500">Comment</span>
                    </div>

                    <div className="flex flex-col items-center gap-1">
                        <div className="relative h-[30px] w-[30px] shrink-0 overflow-hidden rounded border border-black/15" style={bgStyle}>
                            <HabboImage
                                habbo={PREVIEW_NAME}
                                hotel="COM"
                                size="a"
                                direction={2}
                                head_direction={2}
                                gesture="sml"
                                headonly
                                className="absolute left-1/2 top-5 max-w-none -translate-x-1/2 -translate-y-1/2"
                            />
                        </div>
                        <span className="text-[11px] text-gray-500">Reply</span>
                    </div>

                    <div className="flex flex-col items-center gap-1">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full border border-black/15" style={bgStyle}>
                            <HabboImage habbo={PREVIEW_NAME} hotel="COM" size="a" direction={2} head_direction={2} gesture="sml" headonly="true" />
                        </div>
                        <span className="text-[11px] text-gray-500">Thread reply</span>
                    </div>

                    <div className="flex flex-col items-center gap-1">
                        <span className="relative inline-block h-[42px] w-[42px] shrink-0 overflow-hidden rounded-full border border-black/10" style={bgStyle}>
                            <HabboImage
                                habbo={PREVIEW_NAME}
                                hotel="COM"
                                size="a"
                                direction={2}
                                head_direction={3}
                                className="absolute left-[5px] top-[-10px] block max-w-none"
                            />
                        </span>
                        <span className="text-[11px] text-gray-500">Article comment</span>
                    </div>
                </div>
            </Occurrence>

            <Occurrence title="Friends" note="one cell at the size its grid gives it">
                <div className="flex flex-wrap items-end gap-5">
                    <div className="flex flex-col items-center gap-1">
                        <TopFriendTile bgStyle={bgStyle} colWidth={WIDE_COL} wide />
                        <span className="text-[11px] text-gray-500">Top friends, wide column</span>
                    </div>

                    <div className="flex flex-col items-center gap-1">
                        <TopFriendTile bgStyle={bgStyle} colWidth={NARROW_COL} wide={false} />
                        <span className="text-[11px] text-gray-500">Top friends, narrow column</span>
                    </div>

                    <div className="flex flex-col items-center gap-1">
                        <FriendsTabTile bgStyle={bgStyle} />
                        <span className="text-[11px] text-gray-500">Friends tab</span>
                    </div>
                </div>
            </Occurrence>
        </div>
    );
}

function PfpBackgrounds({ userData }) {
    const [backgrounds, setBackgrounds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState({ bgId: '', name: '' });
    const [file, setFile] = useState(null);
    const [filePreview, setFilePreview] = useState('');
    const [saving, setSaving] = useState(false);

    const fileInputRef = useRef(null);
    const isCreating = selected === 'new';

    const load = useCallback(async () => {
        setLoading(true);
        const res = await getAdminCircleBgs().catch(() => null);
        if (res?.success) setBackgrounds(res.backgrounds || []);
        else toast.error('Could not load PFP backgrounds');
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => () => { if (filePreview) URL.revokeObjectURL(filePreview); }, [filePreview]);

    const open = (bg) => {
        setSelected(bg.bgId);
        setForm({ bgId: bg.bgId, name: bg.name || '' });
        setFile(null);
        setFilePreview('');
    };

    const startNew = () => {
        setSelected('new');
        setForm({ bgId: '', name: '' });
        setFile(null);
        setFilePreview('');
    };

    const chooseFile = (e) => {
        const chosen = e.target.files?.[0];
        if (!chosen) return;
        if (filePreview) URL.revokeObjectURL(filePreview);
        setFile(chosen);
        setFilePreview(URL.createObjectURL(chosen));
    };

    const current = backgrounds.find(b => b.bgId === selected) || null;
    const previewUrl = filePreview || (current ? resolveCosmeticUrl(current.imageURL) : '');

    const bgStyle = previewUrl
        ? { backgroundImage: `url(${previewUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
        : { backgroundColor: '#ffffff' };

    const save = async () => {
        const bgId = form.bgId.trim().toUpperCase();
        if (!/^[A-Za-z0-9_]{1,16}$/.test(bgId)) return toast.error('ID must be 1-16 letters, numbers or underscores');
        if (!form.name.trim()) return toast.error('Give the background a name');
        if (isCreating && !file) return toast.error('Choose an image to upload');

        setSaving(true);
        const res = await saveCircleBg({ bgId, name: form.name.trim(), file, isNew: isCreating })
            .catch(e => ({ success: false, message: e?.message }));
        setSaving(false);

        if (res?.success) {
            toast.success(isCreating ? 'Background created' : 'Background saved');
            setFile(null);
            setFilePreview('');
            setSelected(bgId);
            await load();
        } else {
            toast.error(res?.message || 'Could not save the background');
        }
    };

    const remove = async () => {
        if (isCreating || !selected) return;
        if (!window.confirm(`Delete "${form.name}"? This cannot be undone.`)) return;

        const res = await deleteCircleBg(selected).catch(e => ({ success: false, message: e?.message }));
        if (res?.success) {
            toast.success('Background deleted');
            setSelected(null);
            await load();
        } else {
            toast.error(res?.message || 'Could not delete the background', { autoClose: 7000 });
        }
    };

    if (!userData || !userData.hasAdminRole) return <StaffNotFound inline />;

    return (
        <div className="flex flex-col gap-3">
            <input ref={fileInputRef} type="file" accept="image/png,image/gif,image/webp,image/jpeg" className="hidden" onChange={chooseFile} />

            <Panel
                title="PFP Backgrounds"
                right={
                    <button type="button" onClick={startNew} className="font-['Jersey_10'] text-[14px] uppercase tracking-[1px] text-gold hover:underline">
                        + New background
                    </button>
                }
            >
                {loading ? (
                    <p className="text-[13px] text-gray-600">Loading...</p>
                ) : backgrounds.length === 0 ? (
                    <p className="text-[13px] text-gray-600">None yet.</p>
                ) : (
                    <div className="flex max-h-[230px] flex-col gap-1 overflow-y-auto pr-1">
                        {backgrounds.map(bg => {
                            const active = selected === bg.bgId;
                            return (
                                <button
                                    key={bg.bgId}
                                    type="button"
                                    onClick={() => open(bg)}
                                    className={`flex items-center gap-3 rounded px-2 py-[6px] text-left transition-all
                                        ${active ? 'bg-maroon text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                                    style={shadow}
                                >
                                    <span
                                        className="h-[30px] w-[30px] shrink-0 rounded border border-black/20 bg-white"
                                        style={{ backgroundImage: `url(${resolveCosmeticUrl(bg.imageURL)})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
                                    />
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate font-['Jersey_10'] text-[16px] uppercase tracking-[1px]">{bg.name}</span>
                                        <span className={`block truncate text-[11px] ${active ? 'text-white/70' : 'text-gray-500'}`}>
                                            {bg.bgId} · {bg.owners} {bg.owners === 1 ? 'owner' : 'owners'}
                                        </span>
                                    </span>
                                    {!bg.uploaded && (
                                        <span className="shrink-0 font-['Jersey_10'] text-[12px] uppercase tracking-[1px] opacity-60">built in</span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}
            </Panel>

            {selected === null ? (
                <Panel title="Editor">
                    <p className="text-[14px] text-gray-700">
                        Pick a background above, or upload a new one. Every place it appears on the site is previewed here.
                    </p>
                </Panel>
            ) : (
                <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
                    <div className="xl:col-span-4">
                        <Panel title={isCreating ? 'New background' : form.name || form.bgId}>
                            <div className="flex flex-col gap-3">
                                <label className="block">
                                    <span className={label}>Background ID</span>
                                    <input
                                        className={`${input} ${isCreating ? '' : 'bg-gray-100'}`}
                                        value={form.bgId}
                                        disabled={!isCreating}
                                        onChange={(e) => setForm({ ...form, bgId: e.target.value.toUpperCase() })}
                                        placeholder="SUMMER"
                                    />
                                </label>

                                <label className="block">
                                    <span className={label}>Name</span>
                                    <input className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Summer Sunset" />
                                </label>

                                <div>
                                    <span className={label}>Image</span>
                                    <div className="flex items-center gap-2">
                                        <div
                                            className="h-[60px] w-[60px] shrink-0 rounded border border-gray-300 bg-[#e5e7eb]"
                                            style={previewUrl ? { backgroundImage: `url(${previewUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => { if (fileInputRef.current) { fileInputRef.current.value = ''; fileInputRef.current.click(); } }}
                                            className={`${btn} bg-gray-200 text-gray-800 hover:bg-gray-300`}
                                            style={shadow}
                                        >
                                            {isCreating ? 'Choose image' : 'Replace image'}
                                        </button>
                                    </div>
                                    {file && <p className="mt-1 text-[12px] text-gray-500">{file.name}</p>}
                                    {!isCreating && current && !current.uploaded && !file && (
                                        <p className="mt-1 text-[12px] text-gray-500">
                                            Built into the website at {current.imageURL}. Uploading here replaces it.
                                        </p>
                                    )}
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    <button type="button" onClick={save} disabled={saving} className={`${btn} bg-maroon text-white disabled:opacity-60`} style={shadow}>
                                        {saving ? 'Saving...' : isCreating ? 'Create' : 'Save changes'}
                                    </button>
                                    {!isCreating && (
                                        <button type="button" onClick={remove} className={`${btn} bg-red-100 text-red-900 hover:bg-red-200`} style={shadow}>
                                            Delete
                                        </button>
                                    )}
                                    <button type="button" onClick={() => setSelected(null)} className={`${btn} bg-gray-200 text-gray-800 hover:bg-gray-300`} style={shadow}>
                                        Close
                                    </button>
                                </div>
                            </div>
                        </Panel>
                    </div>

                    <div className="xl:col-span-8">
                        <Panel title="Where it shows">
                            <BackgroundPreviews bgStyle={bgStyle} />
                        </Panel>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PfpBackgrounds;
